import { useCallback } from "react";
import { CoxAnalyticsManager } from "@coxauto-ui/core-analytics-cox-analytics";

interface CoxAnalyticsHookReturn {
  pushEvent: (data: Record<string, unknown>) => Promise<void>;
}

/**
 * Wait for CoxAnalyticsManager to be initialized with a timeout
 */
const waitForInitialization = async (
  maxWaitMs = 5000,
  pollIntervalMs = 100
): Promise<boolean> => {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    if (CoxAnalyticsManager.isInitialized()) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  return false;
};

/**
 * Custom hook for CDL Analytics tracking.
 */
export function useCoxAnalytics(): CoxAnalyticsHookReturn {
  const pushEvent = useCallback(async (data: Record<string, unknown>) => {
    // If not initialized, wait for up to 5 seconds for initialization
    if (!CoxAnalyticsManager.isInitialized()) {
      const isInitialized = await waitForInitialization();

      if (!isInitialized) {
        return; // Exit early if not initialized
      }
    }

    try {
      await CoxAnalyticsManager.push(data);
    } catch (error) {
      // Silent error handling - could add proper error reporting here if needed
    }
  }, []);

  return { pushEvent };
}

export default useCoxAnalytics;
